import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import styled from 'styled-components';

import Layout from '../components/Layout/Layout';
import { SEO, SEO_PAGE_TYPES } from '../components/SEO/SEO';
import Paragraphs from '../components/Paragraphs/Paragraphs';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.color('grey_light')};
`;

const Index = ({ data }) => {
  const {
    allContentfulPageLandingPage: { edges },
    allContentfulPageArticle: { edges: newsEdges },
    allContentfulCart: { edges: moneyBuys }
  } = data;

  const {
    paragraphs,
    removeDonateLink,
    cartId: landingPageCartId
  } = edges[0].node;

  // NB: this component represents the homepage - while it *is* a 'Landing Page'
  // CMS content type, it's separated out from using Landing.js via this filter
  // in the associated query below: (filter: {path: {eq: "homepage"}})

  return (
    <Layout
      theme="comicrelief"
      removeDonateLink={removeDonateLink}
      landingPageCartId={landingPageCartId}
    >
      <Wrapper data-test="homepage">
        {paragraphs && (
          <Paragraphs
            theme="comicrelief"
            paragraphs={paragraphs}
            moneyBuys={moneyBuys}
            articles={newsEdges}
          />
        )}
      </Wrapper>
    </Layout>
  );
};

Index.propTypes = {
  data: PropTypes.shape({
    allContentfulPageLandingPage: PropTypes.shape({
      edges: PropTypes.arrayOf(PropTypes.shape)
    }).isRequired,
    allContentfulPageArticle: PropTypes.shape({
      edges: PropTypes.arrayOf(PropTypes.shape)
    }).isRequired,
    allContentfulCart: PropTypes.shape({
      edges: PropTypes.arrayOf(PropTypes.shape)
    }).isRequired
  }).isRequired
};

export default Index;

export const Head = props => (
  <SEO
    title="A just world free from poverty"
    allData={props}
    pageType={SEO_PAGE_TYPES.LANDING}
  >
    {(thisSiteUrl, linkedDataObj) => [
      <script type="application/ld+json" key="website">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          url: thisSiteUrl,
          potentialAction: {
            '@type': 'SearchAction',
            target: `${thisSiteUrl}/search?query={searchbox_target}`,
            'query-input': 'required name=searchbox_target'
          }
        })}
      </script>,
      <script type="application/ld+json" key="organization">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          url: thisSiteUrl,
          logo: `${thisSiteUrl}/assets/logo.jpg`
        })}
      </script>,
      <script type="application/ld+json" key="carousel">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: linkedDataObj?.theseArticleEdges.map((article, position) => ({
            '@type': 'ListItem',
            position,
            url: `${thisSiteUrl}/news/${article.node.path}`
          }))
        })}
      </script>
    ]}
  </SEO>
);

export const queryCards = graphql`
  fragment Picture on ContentfulAsset {
    description
    gatsbyImageData(layout: FULL_WIDTH, quality: 80, placeholder: BLURRED)
  }

  fragment Picture__PeopleList on ContentfulAsset {
    description
    gatsbyImageData(
      layout: FULL_WIDTH
      width: 360
      aspectRatio: 1
      quality: 80
      placeholder: BLURRED
      resizingBehavior: FILL
    )
  }

  fragment Picture__Person on ContentfulAsset {
    description
    gatsbyImageData(
      layout: FULL_WIDTH
      aspectRatio: 1.75
      quality: 80
      placeholder: BLURRED
      resizingBehavior: FILL
      cropFocus: FACE
    )
  }

  # To be reviewed; performant enough currently
  fragment Picture__Dynamic_Gallery on ContentfulAsset {
    description
    gatsbyImageData(
      layout: FIXED
      width: 500
      height: 500
      quality: 75
      placeholder: BLURRED
    )
  }

  fragment Picture__HeroBannerLogo on ContentfulAsset {
    description
    gatsbyImageData(
      layout: FIXED
      width: 200
      quality: 80
    )
  }

  fragment Picture__VideoPoster on ContentfulAsset {
    posterImageData: gatsbyImageData(
      layout: FIXED
      quality: 80
      width: 1440
      placeholder: BLURRED
    )
  }

  fragment Accordion on ContentfulAccordion {
    __typename
    contentful_id
    title
    body {
      raw
    }
  }

  fragment HeroBanner on ContentfulHeroBanner {
    __typename
    contentful_id
    componentBackgroundColour
    openCtaInNewTab
    paddingAbove
    paddingBelow
    pageBackgroundColour
    copyOrientation
    ctaColourTextBannerOnly
    ctaText
    ctaUrl
    type
    loopAmbientVideo
    lVideo {
      file {
        url
      }
    }
    smVideo {
      file {
        url
      }
    }
    body {
      raw
    }
    lImage {
      ...Picture
      ...Picture__VideoPoster
      file {
        url
      }
    }
    mImage {
      ...Picture
      ...Picture__VideoPoster
      file {
        url
      }
    }
    sImage {
      ...Picture
      ...Picture__VideoPoster
    }
    logo {
      ...Picture__HeroBannerLogo
    }
  }

  fragment EmailBanner on ContentfulEmailBanner {
    __typename
    contentful_id
    widgetType
    orientation
    paddingAbove
    paddingBelow
    pageBackgroundColour
    componentBackgroundColourTextVariantOnly
    title
    bodyCopy {
      raw
    }
    privacyCopy {
      raw
    }
    successCopy {
      raw
    }
    ctaText
    campaignCode
    loopAmbientVideo
    lVideo {
      file {
        url
      }
    }
    smVideo {
      file {
        url
      }
    }
    databaseTag
    lImage {
      ...Picture
      ...Picture__VideoPoster
      file {
        url
      }
    }
    mImage {
      ...Picture
      ...Picture__VideoPoster
      file {
        url
      }
    }
    sImage {
      ...Picture
      ...Picture__VideoPoster
    }
  }

  fragment MapParagraph on ContentfulParagraphMap {
    __typename
    contentful_id
    id
    name
    mapType
    backgroundColour
  }

  fragment WhatYourMoneyDoesCarousel on ContentfulWhatYourMoneyDoesCarousel {
    __typename
    contentful_id
    headerCopy
    peopleHelpedText
    mobileHeight
    tabletHeight
    desktopHeight
    autoPlay
    backgroundColour
    paddingAbove
    paddingBelow
    node1Copy
    node1Amount
    node1Image {
      file {
        url
      }
    }
    node2Copy
    node2Amount
    node2Image {
      file {
        url
      }
    }
    node3Copy
    node3Amount
    node3Image {
      file {
        url
      }
    }
    node4Copy
    node4Amount
    node4Image {
      file {
        url
      }
    }
    node5Copy
    node5Amount
    node5Image {
      file {
        url
      }
    }
    node6Copy
    node6Amount
    node6Image {
      file {
        url
      }
    }
    node7Copy
    node7Amount
    node7Image {
      file {
        url
      }
    }
    node8Copy
    node8Amount
    node8Image {
      file {
        url
      }
    }
    node9Copy
    node9Amount
    node9Image {
      file {
        url
      }
    }
  }

  fragment RichtextCarousel on ContentfulRichtextCarousel {
    __typename
    contentful_id
    name
    headingCopy {
      raw
    }
    nodes {
      name
      copy {
        raw
      }
    }
    mobileHeight
    tabletHeight
    desktopHeight
    autoPlay
    paddingAbove
    paddingBelow
    rowBackgroundColour
    carouselBackgroundColour
    nodeBackgroundColour
    nodeOutlineColour
  }

  fragment Asset on ContentfulAsset {
    __typename
    contentful_id
    title
    ...Picture
    file {
      url
    }
  }

  fragment HtmlCode on ContentfulHtmlCode {
    __typename
    contentful_id
    snippetCode {
      snippetCode
    }
  }

  fragment Html on ContentfulHtml {
    __typename
    contentful_id
    snippetCode {
      snippetCode
    }
  }

  fragment Link on ContentfulComponentLink {
    __typename
    contentful_id
    id
    title
    url
    sys {
      type
    }
  }

  fragment LinkPageDefault on ContentfulPageDefault {
    contentful_id
    id
    path
    title
  }

  fragment LinkPageArticle on ContentfulPageArticle {
    contentful_id
    id
    path
    title
    type
  }

  fragment LinkLandingPage on ContentfulPageLandingPage {
    contentful_id
    id
    path
    title
    cartId
  }

  fragment BitesizeParagraph on ContentfulBiteSize {
    __typename
    title
    body {
      raw
    }
    ctaLabel
    ctaLink
    paddingAbove
    paddingBelow
  }

  fragment CardsParagraph on ContentfulParagraphCards {
    __typename
    title
    backgroundColour
    paddingAbove
    paddingBelow
    smallBreakpointRowLayout
    mediumBreakpointRowLayout
    layout
    cards {
      id
      title
      backgroundColour
      label
      centredText
      squaredCorners
      body {
        raw
      }
      image {
        ...Picture
      }
    }
  }

  fragment CtaMultiCard on ContentfulCtaMultiCard {
    __typename
    backgroundColour
    layout
    paddingAbove
    paddingBelow
    cards {
      id
      title
      link
      linkLabel
      body {
        raw
      }
      image {
        ...Picture
      }
    }
  }

  fragment CtaSingleCard on ContentfulCtaSingleCard {
    __typename
    id
    contentfulId: contentful_id
    label
    backgroundColour
    ctaLink
    ctaLabel
    body {
      raw
    }
    image {
      ...Picture
    }
    paddingAbove
    paddingBelow
  }

  fragment FeaturedNewsParagraph on ContentfulParagraphFeaturedNews {
    __typename
    name
    title
    hideTitle
    backgroundColour
    paddingAbove
    paddingBelow
    featuredCategory {
      title
    }
  }

  fragment PartnerListParagraph on ContentfulPartnersList {
    __typename
    ... on ContentfulPartnersList {
      title
      id
      paddingAbove
      paddingBelow
      partnerCategory {
        title
      }

    }
  }

  fragment HTMLParagraph on ContentfulParagraphHtml {
    __typename
    name
    id
    snippetCode {
      snippetCode
    }
  }

  fragment PartnerList on ContentfulPagePartner {
    __typename
    id
    name
    weight
    url
    path
    category
    paragraphs {
      ...PartnerListParagraph
      ...SingleMessageParagraph
      ...CardsParagraph
      ...IframeParagraph
      ...CtaMultiCard
      ...CtaSingleCard
      ...BitesizeParagraph
      ...HeroBanner
      ...DonateBanner
      ...EmailBanner
    }
    summary {
      summary
    }
    body {
      raw
    }
    logo {
      ...Picture
    }
    group {
      title
    }
    backgroundColour
  }

  fragment PressReleasesListParagraph on ContentfulPressReleasesList {
    __typename
    title
  }

  fragment ArticleList on ContentfulPageArticle {
    __typename
    id
    path
    title
    type
    displayDate(formatString: "Do MMMM YYYY")
    categories {
      title
    }
    body {
      raw
    }
    summary {
      summary
    }
    image {
      ...Picture
    }
  }

  fragment PeopleListParagraph on ContentfulPeopleList {
    __typename
    ... on ContentfulPeopleList {
      title
    }
  }

  fragment IframeParagraph on ContentfulParagraphIframe {
    __typename
    id
    title
    backgroundColour
    embedLink
    nonProductionEmbedLink
    initialHeight
    iframeLoaded
    body {
      raw
    }
    placeholderImage {
      ...Picture
    }
    placeholderImageAltText
  }

  fragment PeopleList on ContentfulPagePerson {
    __typename
    id
    title
    type
    role
    path
    image {
      ...Picture__PeopleList
    }
  }

  fragment DonateBanner on ContentfulDonateBanner {
    __typename
    id
    cartId
    clientId
    donationUrl
    donateWidgetIsTextOnly
    donateOrientation
    paddingAbove
    paddingBelow
    pageBackgroundColour
    componentBackgroundColour
    title
    subtitle
    monthlyTitle
    monthlySubtitle
    popUpText
    otherAmountText
    monthlyOtherAmountText
    hideMoneyBuys
    loopAmbientVideo
    lVideo {
      file {
        url
      }
    }
    smVideo {
      file {
        url
      }
    }
    lImage {
      ...Picture
      ...Picture__VideoPoster
      file {
        url
      }
    }
    mImage {
      ...Picture
      ...Picture__VideoPoster
      file {
        url
      }
    }
    sImage {
      ...Picture
      ...Picture__VideoPoster
    }
  }

  fragment SingleMessageParagraph on ContentfulSingleMessage {
    __typename
    id
    title
    copyFirst
    centredText
    fullImage
    youTubeVideoId
    landscapeVideo
    image {
      ...Picture
    }
    secondaryImage {
      ...Picture
    }
    verticalHeight
    label
    text {
      raw
      references {
        ...Asset
        ...HtmlCode
        ...Link
        ...LinkPageDefault
        ...LinkPageArticle
        ...LinkLandingPage
        ...Accordion
      }
    }
    backgroundColour
    paddingAbove
    paddingBelow
    paddingAboveSmBreakpointOnly
    paddingBelowSmBreakpointOnly
  }

  fragment ImpactSlider on ContentfulImpactSlider {
    __typename
    contentful_id
    id
    body {
      raw
    }
    sliderHeading
    defaultSliderValue
    increment
    maximum
    cartId
    opacityAnimation
    paddingAbove
    paddingBelow
    moneybuy1Description
    moneybuy1PriceDecimal
    moneybuy2Description
    moneybuy2PriceDecimal
    moneybuy3Description
    moneybuy3PriceDecimal
    moneybuy4Description
    moneybuy4PriceDecimal
    whiteBackground
    moneybuy4Image {
      file {
        url
      }
    }
    moneybuy3Image {
      file {
        url
      }
    }
    moneybuy2Image {
      file {
        url
      }
    }
    moneybuy1Image {
      file {
        url
      }
    }
    children {
      id
    }
  }

  fragment DescriptorParagraph on ContentfulParagraphDescriptors {
    __typename
    ... on ContentfulParagraphDescriptors {
      title
    }
  }

  fragment Descriptor on ContentfulDescriptor {
    ... on ContentfulDescriptor {
      __typename
      id
      category
      title
      date(formatString: "Do MMMM YYYY")
      tags
      description {
        raw
        references {
          __typename
          contentful_id
          title
          file {
            url
          }
        }
      }
    }
  }

  fragment DynamicGallery on ContentfulDynamicGallery {
    __typename
    contentful_id
    id
    title
    pageBackgroundColour
    textColour
    gridWidth
    loadingBehaviour
    displayOrder
    imageRatio
    useLightboxForImages
    cardNodes {
      id
      title
      body {
        raw
      }
      caption
      centeredText
      image {
        ...Picture__Dynamic_Gallery
      }
    }
    paddingAbove
    paddingBelow
  }

  fragment QuoteSlice on ContentfulQuoteSlice {
    __typename
    contentful_id
    id
    title
    heading {
      heading
    }
    body {
      body
    }
    imageL {
      ...Picture
    }
    imageM {
      ...Picture
    }
    imageS {
      ...Picture
    }
    ctaLabel
    ctaUrl
    pageBackgroundColour
    textColour
    paddingAbove
    paddingBelow
  }

  
  fragment StatsSlice on ContentfulStatsSlice {
    __typename
    contentful_id
    id
    title
    pageBackgroundColour
    statNodes {
      id
      title
      stat
      body {
        raw
      }
    }
    animation
    paddingAbove
    paddingBelow
  }

  query getHomepage {
    allContentfulPageLandingPage(filter: { path: { eq: "homepage" } }) {
      edges {
        node {
          path
          title
          cartId
          removeDonateLink
          imageSeo {
            ...Picture
          }
          summary {
            summary
          }
          paragraphs {
            ...PartnerListParagraph
            ...PeopleListParagraph
            ...SingleMessageParagraph
            ...CardsParagraph
            ...DonateBanner
            ...FeaturedNewsParagraph
            ...IframeParagraph
            ...CtaMultiCard
            ...CtaSingleCard
            ...DescriptorParagraph
            ...BitesizeParagraph
            ...HTMLParagraph
            ...ImpactSlider
            ...MapParagraph
            ...WhatYourMoneyDoesCarousel
            ...HeroBanner
            ...DynamicGallery
            ...QuoteSlice
            ...StatsSlice
            ...EmailBanner
          }
        }
      }
    }
    allContentfulCart {
      edges {
        node {
          cartId
          contentful_id
          singleGiving {
            moneybuys {
              description
              value
            }
          }
          regularGiving {
            moneybuys {
              description
              value
            }
          }
        }
      }
    }
    allContentfulPageArticle(
      limit: 3
      sort: { displayDate: DESC }
      filter: {
        type: { eq: "News Article" }
        image: { title: { glob: "*" } }
        excludeFromFeatureArticles: { ne: "Yes" }
      }
    ) {
      edges {
        node {
          id
          title
          path
          displayDate(formatString: "Do MMMM YYYY")
          body {
            raw
          }
          image {
            ...Picture
          }
        }
      }
    }
  }
`;
